import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LOOP_PREFETCH_MARGIN, LOOP_SWIPE_THRESHOLD_PX } from '@/constants/contentFeed';
import { TContent } from '@/types/Content.types';

type Params = {
  items: TContent[];
  anchorId: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
  onExit: () => void;
};

// Position within the loop, and every way the user can change it: arrow keys, swipe, and
// the on-screen buttons. Kept out of the shell so that component is just layout.
const useLoopNavigation = ({ items, anchorId, hasNextPage, isFetchingNextPage, fetchNextPage, onExit }: Params) => {
  const [index, setIndex] = useState(0);

  const goNext = useCallback(() => setIndex((i) => Math.min(i + 1, items.length - 1)), [items.length]);
  const goPrevious = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);

  // Entry positions the loop at the tapped card. The backend seeds the stream with the
  // anchor, but if it lands elsewhere, honour the anchor rather than starting at 0.
  const anchorIndex = useMemo(() => items.findIndex((item) => item.id === anchorId), [items, anchorId]);
  const hasPositionedRef = useRef(false);
  useEffect(() => {
    if (hasPositionedRef.current || anchorIndex < 0) return;
    hasPositionedRef.current = true;
    setIndex(anchorIndex);
  }, [anchorIndex]);

  // Pull more of the stream in before the user reaches the end. Continuation follows the
  // backend's next-cursor (hasNextPage), fetching ahead until the draw is exhausted.
  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && index >= items.length - LOOP_PREFETCH_MARGIN) fetchNextPage();
  }, [index, items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Keyboard navigation. Escape leaves the loop.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Never hijack arrow keys while the user is typing or inside a control.
      const target = event.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;

      if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        event.preventDefault();
        goNext();
      } else if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        goPrevious();
      } else if (event.key === 'Escape') {
        onExit();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [goNext, goPrevious, onExit]);

  // Touch swipe. Anything below the threshold is an accidental touch, not a gesture.
  const touchStartRef = useRef<number | null>(null);
  const onTouchStart = useCallback((event: React.TouchEvent) => {
    touchStartRef.current = event.touches[0].clientY;
  }, []);
  const onTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      if (start === null) return;

      const delta = start - event.changedTouches[0].clientY;
      if (Math.abs(delta) < LOOP_SWIPE_THRESHOLD_PX) return;
      if (delta > 0) goNext();
      else goPrevious();
    },
    [goNext, goPrevious],
  );

  return {
    index,
    current: items[index] as TContent | undefined,
    canGoNext: index < items.length - 1,
    canGoPrevious: index > 0,
    goNext,
    goPrevious,
    onTouchStart,
    onTouchEnd,
  };
};

export default useLoopNavigation;
